import React from 'react';
import { Info, FileText, TrendingUp, AlertTriangle, Briefcase, Zap, Award } from 'lucide-react';

/**
 * Компонент с информацией о банкротстве и финансовой устойчивости
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-orange-500" />
        Банкротство предприятий: причины, признаки и профилактика
      </h2>
      
      <div className="prose prose-orange dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-500" />
          Что такое банкротство?
        </h3>
        <p>
          <strong>Банкротство</strong> (несостоятельность) — это признанная арбитражным судом неспособность должника в полном объеме удовлетворить требования кредиторов по денежным обязательствам, о выплате выходных пособий и/или об оплате труда лиц, работающих или работавших по трудовому договору, и/или исполнить обязанность по уплате обязательных платежей.
        </p>
        <p>
          В Российской Федерации процедура банкротства регулируется Федеральным законом №127-ФЗ «О несостоятельности (банкротстве)» от 26.10.2002 с последующими изменениями и дополнениями. Этот закон определяет порядок и условия признания должника банкротом, а также порядок и условия осуществления мер по предупреждению несостоятельности.
        </p>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-500" />
          Ключевые модели оценки риска банкротства
        </h3>
        <p>
          Существует множество методик для прогнозирования вероятности банкротства предприятий. Наиболее распространенные из них:
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-orange-50 dark:bg-orange-900/20">
                <th className="px-4 py-2 text-left font-semibold text-orange-700 dark:text-orange-300">Модель</th>
                <th className="px-4 py-2 text-left font-semibold text-orange-700 dark:text-orange-300">Описание</th>
                <th className="px-4 py-2 text-left font-semibold text-orange-700 dark:text-orange-300">Применимость</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-2 align-top font-medium">Модель Альтмана (Z-счет)</td>
                <td className="px-4 py-2 align-top">Первая модель, разработанная в 1968 году. Z-счет объединяет 5 финансовых коэффициентов с разными весами.</td>
                <td className="px-4 py-2 align-top">Наиболее точна для крупных производственных компаний, котирующихся на бирже.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 align-top font-medium">Модель Фулмера</td>
                <td className="px-4 py-2 align-top">Учитывает 9 различных финансовых показателей, что делает ее более детализированной.</td>
                <td className="px-4 py-2 align-top">Хорошо подходит для малых и средних предприятий различных отраслей.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 align-top font-medium">Модель Спрингейта</td>
                <td className="px-4 py-2 align-top">Разработана в 1978 г. на основе модели Альтмана. Использует 4 финансовых коэффициента.</td>
                <td className="px-4 py-2 align-top">Универсальна, но наиболее эффективна для промышленных предприятий.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 align-top font-medium">Модель Таффлера</td>
                <td className="px-4 py-2 align-top">Британская модель, разработанная для оценки компаний, торгующихся на бирже.</td>
                <td className="px-4 py-2 align-top">Хорошо работает для публичных компаний и предприятий торговли.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 align-top font-medium">Модель Сайфуллина-Кадыкова</td>
                <td className="px-4 py-2 align-top">Российская модель, адаптированная к особенностям отечественной экономики.</td>
                <td className="px-4 py-2 align-top">Специально разработана для российских предприятий с учетом отечественной специфики.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800 my-6">
          <h4 className="text-orange-700 dark:text-orange-300 font-medium flex items-center gap-2">
            <Award className="w-4 h-4" />
            Оптимальный подход к оценке
          </h4>
          <p className="text-orange-600 dark:text-orange-400 text-sm mb-0">
            Для наиболее точной оценки риска банкротства рекомендуется использовать несколько различных моделей одновременно, учитывая специфику отрасли и размер предприятия. Комплексный подход позволяет нивелировать недостатки отдельных методик.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Признаки приближающегося банкротства
        </h3>
        <p>
          Финансовые трудности компании обычно не возникают внезапно. Существует ряд ранних индикаторов, позволяющих выявить проблемы до наступления критической ситуации:
        </p>
        <ul>
          <li><strong>Наблюдение</strong> — процедура, применяемая к должнику в целях обеспечения сохранности его имущества, проведения анализа финансового состояния, составления реестра требований кредиторов и проведения первого собрания кредиторов.</li>
          <li><strong>Финансовое оздоровление</strong> — процедура, применяемая к должнику в целях восстановления его платежеспособности и погашения задолженности в соответствии с графиком.</li>
          <li><strong>Внешнее управление</strong> — процедура, применяемая к должнику в целях восстановления его платежеспособности, с передачей полномочий по управлению должником внешнему управляющему.</li>
          <li><strong>Конкурсное производство</strong> — процедура, применяемая к должнику, признанному банкротом, в целях соразмерного удовлетворения требований кредиторов.</li>
          <li><strong>Мировое соглашение</strong> — процедура, применяемая на любой стадии рассмотрения дела о банкротстве в целях прекращения производства по делу о банкротстве путем достижения соглашения между должником и кредиторами.</li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium">Ранняя диагностика — ключ к спасению</h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            Многочисленные исследования показывают, что шансы на успешное финансовое оздоровление наиболее высоки при выявлении проблем на ранней стадии. Регулярный мониторинг финансового состояния с помощью различных моделей оценки риска банкротства позволяет принять меры до наступления критической фазы.
          </p>
        </div>

        <h3>Заключение</h3>
        <p>
          Банкротство — это серьезный правовой и экономический процесс, который имеет значительные последствия для всех заинтересованных сторон: самой компании, ее владельцев, сотрудников, кредиторов и клиентов. Однако при своевременном выявлении проблем и принятии адекватных мер многих банкротств можно избежать.
        </p>
        <p>
          Наш калькулятор риска банкротства предоставляет удобный инструмент для первичной диагностики финансового состояния предприятия и определения уровня риска несостоятельности. Однако следует помнить, что результаты такой оценки носят ориентировочный характер и должны использоваться как часть комплексного анализа в сочетании с профессиональной консультацией.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;